import { useMemo, useState } from 'react';
import { Callout, Card, Checkbox, Input, Toolbar, formatMoney, formatQty } from '@biovet/ui';
import { RecordGrid } from './Records.js';
import { ArAgeingScreen, ApAgeingScreen } from './CashAgeing.js';
import { DateWindow, useDebounced } from './parts.js';
import { companyToday, monthOf } from '../lib/reference-data.js';
import {
  useResource,
  type ApiContext,
  type AuditEntityRef,
  type AuditTrailRow,
  type GrossMarginRow,
  type SalesByZoneRow,
} from '../lib/api.js';
import { addDecimal } from '@biovet/ui';
import type { RecordColumn, RecordRow, RecordSet } from '../lib/design-data.js';


const R = 'right' as const;

type Window = { label: string; from?: string; to?: string };

function useWindows(): Window[] {
  const { data } = useResource<ApiContext>('/context');
  const fy = data?.fiscalYear;
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + '01';

  return useMemo(
    () => [
      { label: 'This month', from: monthStart, to: today },
      ...(fy ? [{ label: `FY ${fy.code}`, from: fy.startDate, to: fy.endDate }] : []),
      { label: 'All time' },
    ],
    [fy, monthStart, today],
  );
}

function windowParams(w: Window | undefined): Record<string, string | undefined> {
  return { from: w?.from, to: w?.to };
}

function describeWindow(w: Window | undefined): string {
  if (!w) return '';
  if (!w.from && !w.to) return 'the whole book';
  return `${w.from} to ${w.to}`;
}

function describeRange(range: { from: string; to: string }): string {
  if (!range.from && !range.to) return 'all time';
  return `${range.from || 'the start of the book'} to ${range.to || 'today'}`;
}


export function SalesByZoneScreen() {
  const windows = useWindows();
  const [chip, setChip] = useState(0);
  const active = windows[chip] ?? windows[0];
  const { data, error, loading } = useResource<{ rows: SalesByZoneRow[] }>(
    '/reports/sales-by-zone',
    windowParams(active),
  );
  const rows = data?.rows ?? [];

  const set = useMemo<RecordSet>(() => {
    const cols: RecordColumn[] = [
      { label: 'Zone / salesperson' },
      { label: 'Invoices', align: R, mono: true, width: '90px' },
      { label: 'Qty (Kg)', align: R, mono: true, width: '120px' },
      { label: 'Net sales', align: R, mono: true, width: '150px' },
      { label: 'Target', align: R, mono: true, width: '150px' },
      { label: 'Achv %', align: R, mono: true, width: '100px' },
      { label: 'Margin %', align: R, mono: true, width: '110px' },
      { label: 'Commission', align: R, mono: true, width: '140px' },
    ];

    const body: RecordRow[] = [];
    let zone: string | null = null;
    for (const r of rows) {
      if (r.zoneName !== zone) {
        zone = r.zoneName;
        body.push({ c: [zone, '', '', '', '', '', '', ''], head: true });
      }
      body.push([
        r.salespersonName,
        String(r.invoices),
        formatQty(r.qtyBase, 0),
        formatMoney(r.netSales, { decimals: 0 }),
        r.targetValue === null ? '—' : formatMoney(r.targetValue, { decimals: 0 }),
        r.achievementPct === null ? '—' : `${r.achievementPct}%`,
        r.marginPct === null ? '—' : `${r.marginPct}%`,
        formatMoney(r.commission, { decimals: 0 }),
      ]);
    }

    if (rows.length > 0) {
      const netSales = rows.reduce((a, r) => addDecimal(a, r.netSales), '0');
      const margin = rows.reduce((a, r) => addDecimal(a, r.margin), '0');
      const commission = rows.reduce((a, r) => addDecimal(a, r.commission), '0');
      const qty = rows.reduce((a, r) => addDecimal(a, r.qtyBase), '0');
      const invoices = rows.reduce((a, r) => a + r.invoices, 0);
      const pct = Number(netSales) === 0 ? null : (100 * Number(margin)) / Number(netSales);
      body.push({
        c: [
          'Total',
          String(invoices),
          formatQty(qty, 0),
          formatMoney(netSales, { decimals: 0 }),
          '',
          '',
          pct === null ? '—' : `${pct.toFixed(2)}%`,
          formatMoney(commission, { decimals: 0 }),
        ],
        bold: true,
        emphasis: true,
      });
    }

    return {
      crumb: 'Reports / Sales',
      title: 'Sales by zone and salesperson',
      subtitle: `Posted sales invoices · ${describeWindow(active)} · net of tax, in base currency`,
      chips: windows.map((w) => w.label),
      cols,
      rows: body,
    };
  }, [rows, windows, active]);

  return (
    <>
      {error && (
        <Callout tone="danger" title="Could not load sales by zone">
          {error}
        </Callout>
      )}
      <RecordGrid
        set={set}
        loading={loading}
        chip={chip}
        onChipChange={setChip}
        emptyTitle="No posted sales invoices in this window"
      />
    </>
  );
}


export function GrossMarginScreen() {
  const windows = useWindows();
  const [chip, setChip] = useState(0);
  const active = windows[chip] ?? windows[0];
  const { data, error, loading } = useResource<{ rows: GrossMarginRow[] }>(
    '/reports/gross-margin',
    windowParams(active),
  );
  const rows = data?.rows ?? [];

  const set = useMemo<RecordSet>(() => {
    const cols: RecordColumn[] = [
      { label: 'SKU', width: '150px', mono: true },
      { label: 'Product' },
      { label: 'Qty (Kg)', align: R, mono: true, width: '120px' },
      { label: 'Cost / Kg', align: R, mono: true, width: '120px' },
      { label: 'Rate / Kg', align: R, mono: true, width: '120px' },
      { label: 'Margin / Kg', align: R, mono: true, width: '120px' },
      { label: 'Margin', align: R, mono: true, width: '150px' },
      { label: 'Margin %', align: R, mono: true, width: '100px' },
    ];

    const body: RecordRow[] = rows.map((r) => [
      r.sku,
      r.productName,
      formatQty(r.qtyBase, 0),
      r.costPerUnit === null ? '—' : formatMoney(r.costPerUnit, { decimals: 2 }),
      r.ratePerUnit === null ? '—' : formatMoney(r.ratePerUnit, { decimals: 2 }),
      r.marginPerUnit === null ? '—' : formatMoney(r.marginPerUnit, { decimals: 2 }),
      formatMoney(r.margin, { decimals: 0 }),
      r.marginPct === null ? '—' : `${r.marginPct}%`,
    ]);

    if (rows.length > 0) {
      const qty = rows.reduce((a, r) => addDecimal(a, r.qtyBase), '0');
      const netSales = rows.reduce((a, r) => addDecimal(a, r.netSales), '0');
      const cogs = rows.reduce((a, r) => addDecimal(a, r.cogs), '0');
      const margin = rows.reduce((a, r) => addDecimal(a, r.margin), '0');
      const per = (total: string) =>
        Number(qty) === 0 ? '—' : (Number(total) / Number(qty)).toFixed(2);
      const pct = Number(netSales) === 0 ? null : (100 * Number(margin)) / Number(netSales);
      body.push({
        c: [
          '',
          'Weighted average',
          formatQty(qty, 0),
          per(cogs),
          per(netSales),
          per(margin),
          formatMoney(margin, { decimals: 0 }),
          pct === null ? '—' : `${pct.toFixed(2)}%`,
        ],
        bold: true,
        emphasis: true,
      });
    }

    return {
      crumb: 'Reports / Management',
      title: 'Gross margin analysis by product',
      subtitle: `What each sale earned once its cost is taken off · ${describeWindow(active)}`,
      chips: windows.map((w) => w.label),
      cols,
      rows: body,
    };
  }, [rows, windows, active]);

  return (
    <>
      {error && (
        <Callout tone="danger" title="Could not load gross margin">
          {error}
        </Callout>
      )}
      <RecordGrid
        set={set}
        loading={loading}
        chip={chip}
        onChipChange={setChip}
        emptyTitle="No posted sales invoices in this window"
      />
    </>
  );
}


const ENTITY_LABEL: Record<string, string> = {
  accounts: 'Account',
  parties: 'Customer / supplier',
  products: 'Product',
  users: 'User',
  roles: 'Role',
  role_permissions: 'Role permission',
  user_roles: 'User role',
  user_scopes: 'User scope',
  user_company_access: 'Company access',
  number_series: 'Number series',
  warehouses: 'Warehouse',
  zones: 'Zone',
  cities: 'City',
  salespersons: 'Salesperson',
  branches: 'Branch',
  companies: 'Company profile',
  tax_codes: 'Tax code',
  price_lists: 'Price list',
  fiscal_years: 'Financial year',
  periods: 'Period',
  commission_rules: 'Commission rule',
  bank_accounts: 'Bank account',
  batches: 'Batch',
  product_barcodes: 'Barcode',
  product_uom_conversions: 'Unit conversion',
  fx_rates: 'FX rate',
  vouchers: 'Voucher',
  voucher_lines: 'Voucher line',
  sales_invoices: 'Sales invoice',
  sales_invoice_lines: 'Sales invoice line',
  sales_returns: 'Credit note',
  purchase_invoices: 'Purchase invoice',
  purchase_invoice_lines: 'Purchase invoice line',
  purchase_returns: 'Debit note',
  import_shipments: 'Consignment',
  stock_transfers: 'Stock transfer',
  stock_adjustments: 'Stock adjustment',
  count_sessions: 'Physical count',
  deals: 'Deal',
  deal_lines: 'Deal line',
  deal_settlements: 'Settlement',
  commission_invoices: 'Commission invoice',
  commission_runs: 'Commission run',
};

const VERB_OF: Record<string, string> = {
  INSERT: 'created',
  DELETE: 'deleted',
};

function noun(table: string): string {
  const words = table.replace(/_/g, ' ').replace(/(?<!s)s$/, '');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function describeRef(ref: AuditEntityRef): string {
  const label = ENTITY_LABEL[ref.entityTable] ?? noun(ref.entityTable);
  const rest = [ref.entityKey, ref.entityName].filter(Boolean).join(' · ');
  return rest ? `${label} · ${rest}` : label;
}

function sameRef(a: AuditEntityRef, b: AuditEntityRef): boolean {
  return a.entityTable === b.entityTable && (a.entityId ?? '') === (b.entityId ?? '');
}

function describeEntity(r: AuditTrailRow): string {
  const lead = describeRef(r);
  const others = r.touched.filter((t) => !sameRef(t, r));
  const parts = [lead, ...others.map(describeRef)];
  const hidden = r.touchedCount - parts.length;
  if (hidden > 0) parts.push(`+${hidden} more`);
  const list = parts.join(', ');
  return r.changes > 1 ? `${list}  (${r.changes} changes)` : list;
}

export function AuditTrailScreen() {
  const [range, setRange] = useState(() => monthOf(companyToday()));
  const [search, setSearch] = useState('');
  const [raw, setRaw] = useState(false);
  const settled = useDebounced(search);
  const { data, error, loading } = useResource<{ rows: AuditTrailRow[] }>('/reports/audit-trail', {
    from: range.from || undefined,
    to: range.to || undefined,
    q: settled.trim() || undefined,
    raw: raw ? '1' : undefined,
    limit: '200',
  });
  const rows = useMemo(() => data?.rows ?? [], [data]);

  const set = useMemo<RecordSet>(() => {
    const cols: RecordColumn[] = [
      { label: 'When', width: '160px', mono: true },
      { label: 'Who', width: '150px' },
      { label: 'Action', width: '100px', badge: true },
      { label: 'What was changed', width: '360px' },
      { label: 'Document', width: '150px', mono: true },
      { label: 'Amount', align: R, mono: true, width: '120px' },
      { label: 'Fields changed', width: '200px' },
      { label: 'IP address', width: '125px', mono: true },
    ];

    const body: RecordRow[] = rows.map((r) => [
      r.loggedAt,
      r.username,
      r.action,
      describeEntity(r),
      r.documentRef ?? '—',
      r.amountBase === null ? '—' : formatMoney(r.amountBase, { decimals: 0 }),
      r.changedKeys && r.changedKeys.length > 0
        ? r.changedKeys.join(', ')
        : (r.reason ?? VERB_OF[r.action] ?? '—'),
      r.ipAddress ?? '—',
    ]);

    return {
      crumb: 'Reports / Compliance',
      title: 'Audit trail',
      subtitle: `${
        raw ? 'Every change written to the log' : 'One row per act, newest first'
      } · ${describeRange(range)}${
        settled.trim() ? ` · matching “${settled.trim()}”` : ''
      } · newest 200`,
      chips: [],
      cols,
      rows: body,
    };
  }, [rows, range, settled]);

  return (
    <>
      {error && (
        <Callout tone="danger" title="Could not load the audit trail">
          {error}
        </Callout>
      )}
      <RecordGrid
        set={set}
        loading={loading}
        emptyTitle="Nothing logged in this window"
        actions={
          <>
            {}
            <Checkbox
              label="Every change"
              checked={raw}
              onChange={(e) => setRaw(e.target.checked)}
              className="mr-1 items-center"
            />
            <Input
              size="sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="User, document, account, IP…"
              aria-label="Search the audit trail"
              className="w-[240px]"
            />
          </>
        }
      >
        <Card flush className="mb-3.5" data-noprint="1">
          <Toolbar>
            <DateWindow range={range} onChange={setRange} label="All time" />
            <div className="flex-1" />
            <span className="text-[12px] text-[var(--ink-3)]">
              {rows.length} entr{rows.length === 1 ? 'y' : 'ies'} · {describeRange(range)}
            </span>
          </Toolbar>
        </Card>
      </RecordGrid>
    </>
  );
}


export function ArApAgeingScreen() {
  return (
    <>
      <ArAgeingScreen />
      <div className="pt-3.5">
        <ApAgeingScreen />
      </div>
    </>
  );
}


interface RegisterRow {
  date: string;
  invoiceNo: string;
  supplierInvoiceNo?: string | null;
  party: string;
  partyNtn: string | null;
  partyStrn: string | null;
  product: string;
  sku: string | null;
  qty: string;
  uom: string | null;
  rate: string;
  amountExTax: string;
  taxPct: string;
  taxAmount: string;
  amountIncTax: string;
  status: string;
}

function RegisterReport({ kind }: { kind: 'sales' | 'purchase' }) {
  const windows = useWindows();
  const [chip, setChip] = useState(0);
  const active = windows[chip] ?? windows[0];
  const sales = kind === 'sales';
  const { data, error, loading } = useResource<{ rows: RegisterRow[] }>(
    sales ? '/reports/sales-register' : '/reports/purchase-register',
    windowParams(active),
  );
  const rows = useMemo(() => data?.rows ?? [], [data]);

  const set = useMemo<RecordSet>(() => {
    const cols: RecordColumn[] = [
      { label: 'Date', width: '96px', mono: true },
      { label: 'Invoice no.', width: '140px', mono: true },
      { label: 'Party name' },
      { label: 'Product' },
      { label: 'Qty', align: R, mono: true, width: '92px' },
      { label: 'Rate', align: R, mono: true, width: '92px' },
      { label: 'Amount excl. tax', align: R, mono: true, width: '134px' },
      { label: 'Sales tax', align: R, mono: true, width: '124px' },
      { label: 'Amount incl. tax', align: R, mono: true, width: '134px' },
    ];

    const body: RecordRow[] = rows.map((r) => [
      r.date.slice(0, 10),
      !sales && r.supplierInvoiceNo && r.supplierInvoiceNo !== r.invoiceNo
        ? `${r.invoiceNo} · ${r.supplierInvoiceNo}`
        : r.invoiceNo,
      r.party,
      r.product,
      `${formatQty(r.qty, 2)}${r.uom ? ` ${r.uom}` : ''}`,
      formatMoney(r.rate, { decimals: 2 }),
      formatMoney(r.amountExTax, { decimals: 2 }),
      `${formatMoney(r.taxAmount, { decimals: 2 })}${
        Number(r.taxPct) > 0 ? `  @${Number(r.taxPct)}%` : ''
      }`,
      formatMoney(r.amountIncTax, { decimals: 2 }),
    ]);

    if (rows.length > 0) {
      const ex = rows.reduce((a, r) => addDecimal(a, r.amountExTax), '0');
      const tax = rows.reduce((a, r) => addDecimal(a, r.taxAmount), '0');
      const inc = rows.reduce((a, r) => addDecimal(a, r.amountIncTax), '0');
      const invoices = new Set(rows.map((r) => r.invoiceNo)).size;
      body.push({
        c: [
          'Total',
          `${invoices} invoice${invoices === 1 ? '' : 's'}`,
          `${rows.length} line${rows.length === 1 ? '' : 's'}`,
          '',
          '',
          '',
          formatMoney(ex, { decimals: 2 }),
          formatMoney(tax, { decimals: 2 }),
          formatMoney(inc, { decimals: 2 }),
        ],
        bold: true,
        emphasis: true,
      });
    }

    return {
      crumb: `Reports / ${sales ? 'Sales' : 'Purchase'}`,
      title: `${sales ? 'Sales' : 'Purchase'} Register`,
      subtitle: `The sales tax on every ${sales ? 'sales' : 'purchase'} invoice line · ${describeWindow(active)}`,
      chips: windows.map((w) => w.label),
      cols,
      rows: body,
    };
  }, [rows, windows, active, sales]);

  return (
    <RecordGrid
      set={set}
      loading={loading}
      error={error}
      chip={chip}
      onChipChange={setChip}
      emptyTitle={`No ${sales ? 'sales' : 'purchase'} invoice in this period`}
    >
      {rows.length === 0 && !loading && !error && (
        <Callout tone="info" title="Nothing to report yet">
          The register is built from posted {sales ? 'sales invoices' : 'purchase invoices'}. None
          falls in {describeWindow(active)} — widen the period, or raise one first.
        </Callout>
      )}
    </RecordGrid>
  );
}

export function SalesRegisterScreen() {
  return <RegisterReport kind="sales" />;
}

export function PurchaseRegisterScreen() {
  return <RegisterReport kind="purchase" />;
}
